import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { FINISHES, CATEGORIES, CONDITIONS, MATERIALS, SURFACE_FINISHES, INDIAN_STATES, fmtDate } from "@/lib/constants";
import StatusBadge from "@/components/StatusBadge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Camera, ImagePlus, X, UserCheck, Loader2, CheckCircle2, Plus, AlertTriangle } from "lucide-react";

const DRAFT_KEY = "reshine_newjob_draft";

const newKey = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

const emptyArticle = () => ({ _key: newKey(), article_name: "", category: "", quantity: "1", base_material: "", weight: "", condition: "", surface_finish: "", finishes: [], finish_other: "" });

function validateJobForm(form, existingCustomer) {
  if (!existingCustomer) {
    if (!form.customer_name.trim()) return "First name is required";
    if (!form.last_name.trim()) return "Surname is required";
    const email = form.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
    if (!form.address.trim()) return "Address is required";
    if (!form.city.trim()) return "City is required";
    if (!form.state.trim()) return "State is required";
    if (!/^\d{6}$/.test(form.pincode.trim())) return "A valid 6-digit pincode is required";
  }
  for (let i = 0; i < form.articles.length; i++) {
    const a = form.articles[i];
    if (!a.article_name.trim()) return `Article ${i + 1}: product name is required`;
    if (!a.category) return `Article ${i + 1}: please select a product category`;
    if (!a.quantity || parseInt(a.quantity) < 1) return `Article ${i + 1}: quantity is required (min 1)`;
    if ((a.finishes || []).length === 0) return `Article ${i + 1}: select at least one required finish / service`;
    if ((a.finishes || []).includes("Other") && !a.finish_other.trim()) return `Article ${i + 1}: please describe the custom finish`;
  }
  return null;
}

export default function NewJob() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preCustomerId = searchParams.get("customer_id");

  const [mobile, setMobile] = useState("");
  const [checking, setChecking] = useState(false);
  const [existingCustomer, setExistingCustomer] = useState(null);
  const [customerChecked, setCustomerChecked] = useState(false);
  const [form, setForm] = useState({
    customer_name: "", last_name: "", whatsapp: "", email: "", address: "", city: "", state: "", pincode: "",
    articles: [emptyArticle()],
    special_instructions: "", estimated_value: "", advance_payment: "",
    expected_completion_date: "", staff_remarks: "", confirmed_finish: false,
  });
  const [photos, setPhotos] = useState([]); // {file, preview}
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [dupCustomer, setDupCustomer] = useState(null);
  const dirtyRef = useRef(false);

  // restore draft
  useEffect(() => {
    if (preCustomerId) {
      api.get(`/customers/${preCustomerId}`).then((r) => {
        setExistingCustomer(r.data);
        setMobile(r.data.mobile);
        setCustomerChecked(true);
      }).catch((e) => console.error("Customer prefetch failed:", e));
      return;
    }
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (draft) {
        const restored = { ...draft.form };
        if (Array.isArray(restored.articles)) {
          restored.articles = restored.articles.map((a) => ({
            ...emptyArticle(), ...a,
            finishes: Array.isArray(a?.finishes) ? a.finishes : [],
          }));
        }
        setForm((f) => ({ ...f, ...restored }));
        setMobile(draft.mobile || "");
        if (Object.keys(draft.form || {}).some((k) => draft.form[k])) {
          toast.info("Draft restored from your last session");
        }
      }
    } catch (e) { console.error("Failed to restore draft:", e); }
  }, [preCustomerId]);

  // autosave draft
  useEffect(() => {
    if (success) return;
    const t = setTimeout(() => {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, mobile }));
    }, 600);
    return () => clearTimeout(t);
  }, [form, mobile, success]);

  // warn before leaving
  useEffect(() => {
    const handler = (e) => {
      if (dirtyRef.current && !success) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [success]);

  const set = (k, v) => { dirtyRef.current = true; setForm((f) => ({ ...f, [k]: v })); };

  const setArticle = (i, k, v) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, articles: f.articles.map((a, idx) => (idx === i ? { ...a, [k]: v } : a)) }));
  };
  const addArticle = () => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, articles: [...f.articles, emptyArticle()] }));
  };
  const removeArticle = (i) => {
    dirtyRef.current = true;
    setForm((f) => ({ ...f, articles: f.articles.filter((_, idx) => idx !== i) }));
  };

  const checkCustomer = async () => {
    if (!/^\d{10,15}$/.test(mobile.trim())) {
      toast.error("Enter a valid mobile number (10 digits)");
      return;
    }
    setChecking(true);
    try {
      const res = await api.get(`/customers/by-mobile/${mobile.trim()}`);
      setExistingCustomer(res.data);
      toast.success(`Existing customer found: ${res.data.customer_name}`);
    } catch (e) {
      if (e?.response?.status === 404) {
        setExistingCustomer(null);
        toast.info("New customer — please fill in their details");
      }
    } finally {
      setCustomerChecked(true);
      setChecking(false);
    }
  };

  // Proactive duplicate guard: detect an existing customer as staff type the mobile
  useEffect(() => {
    if (existingCustomer) { setDupCustomer(null); return; }
    const m = mobile.trim();
    if (!/^\d{10,15}$/.test(m)) { setDupCustomer(null); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await api.get(`/customers/by-mobile/${m}`);
        if (!cancelled) setDupCustomer(res.data);
      } catch { if (!cancelled) setDupCustomer(null); }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [mobile, existingCustomer]);

  const useDuplicateCustomer = () => {
    setExistingCustomer(dupCustomer);
    setCustomerChecked(true);
    setDupCustomer(null);
    toast.success(`Using existing customer: ${dupCustomer.customer_name}`);
  };

  const addPhotos = (files) => {
    const list = Array.from(files || []).filter((f) => f.type.startsWith("image/"));
    setPhotos((p) => [...p, ...list.map((file) => ({ file, preview: URL.createObjectURL(file) }))]);
    dirtyRef.current = true;
  };

  const removePhoto = (idx) => {
    setPhotos((p) => {
      URL.revokeObjectURL(p[idx].preview);
      return p.filter((_, i) => i !== idx);
    });
  };

  const toggleArticleFinish = (i, f) => {
    dirtyRef.current = true;
    setForm((prev) => ({
      ...prev,
      articles: prev.articles.map((a, idx) =>
        idx === i
          ? { ...a, finishes: (a.finishes || []).includes(f) ? (a.finishes || []).filter((x) => x !== f) : [...(a.finishes || []), f] }
          : a
      ),
    }));
  };

  const submit = async () => {
    if (submitting) return;
    const validationError = validateJobForm(form, existingCustomer);
    if (validationError) return toast.error(validationError);
    if (photos.length === 0) return toast.error("At least one product photograph is required");

    setSubmitting(true);
    try {
      const payload = {
        existing_customer_id: existingCustomer?.id || null,
        customer_name: existingCustomer ? undefined : `${form.customer_name.trim()} ${form.last_name.trim()}`.trim(),
        mobile: existingCustomer ? undefined : mobile.trim(),
        whatsapp: form.whatsapp.trim(), email: form.email.trim(), address: form.address.trim(),
        city: form.city.trim(), state: form.state.trim(), pincode: form.pincode.trim(),
        articles: form.articles.map((a) => ({
          article_name: a.article_name.trim(), category: a.category, quantity: parseInt(a.quantity),
          base_material: a.base_material, weight: a.weight.trim(), condition: a.condition, surface_finish: a.surface_finish,
          required_finishes: a.finishes || [], finish_other: a.finish_other.trim(),
        })),
        special_instructions: form.special_instructions.trim(),
        estimated_value: form.estimated_value ? parseFloat(form.estimated_value) : null,
        advance_payment: form.advance_payment ? parseFloat(form.advance_payment) : null,
        expected_completion_date: form.expected_completion_date,
        staff_remarks: form.staff_remarks.trim(), confirmed_finish: form.confirmed_finish,
      };
      const res = await api.post("/jobs", payload);
      const job = res.data;
      const fd = new FormData();
      photos.forEach((p) => fd.append("files", p.file));
      fd.append("photo_type", "Before");
      await api.post(`/jobs/${job.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      localStorage.removeItem(DRAFT_KEY);
      dirtyRef.current = false;
      setSuccess(job);
      toast.success(`Job ${job.job_number} registered`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create job. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="max-w-lg mx-auto text-center pt-8" data-testid="job-success-screen">
        <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto" />
        <h1 className="font-display text-2xl sm:text-3xl font-semibold mt-4">Job Successfully Registered</h1>
        <Card className="mt-6 text-left border-primary/30">
          <CardContent className="p-5 space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Job Number</p>
            <p className="font-display text-3xl font-bold" data-testid="success-job-number">{success.job_number}</p>
            <div className="pt-2 space-y-1 text-sm">
              <p><span className="text-muted-foreground">Customer:</span> <span className="font-medium" data-testid="success-customer-name">{success.customer_name}</span></p>
              <p><span className="text-muted-foreground">Article(s):</span> <span className="font-medium">{(success.articles || []).map((a) => `${a.article_name} ×${a.quantity}`).join(", ") || success.article_name}</span></p>
              <p><span className="text-muted-foreground">Required Finish:</span> <span className="font-medium">{success.required_finishes.join(", ")}{success.finish_other ? ` (${success.finish_other})` : ""}</span></p>
              {success.estimated_value != null && (
                <p data-testid="success-payment-summary"><span className="text-muted-foreground">Payment:</span> <span className="font-medium">Est. ₹{success.estimated_value} · Advance ₹{success.advance_payment || 0} · <span className="text-amber-800 dark:text-amber-300 font-semibold">Balance ₹{success.estimated_value - (success.advance_payment || 0)}</span></span></p>
              )}
            </div>
          </CardContent>
        </Card>
        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="flex-1 h-12" onClick={() => { setSuccess(null); setPhotos([]); setForm({ customer_name: "", last_name: "", whatsapp: "", email: "", address: "", city: "", state: "", pincode: "", articles: [emptyArticle()], special_instructions: "", estimated_value: "", advance_payment: "", expected_completion_date: "", staff_remarks: "", confirmed_finish: false }); setExistingCustomer(null); setCustomerChecked(false); setMobile(""); }} data-testid="register-another-job-button">
            + New Job
          </Button>
          <Button className="flex-1 h-12 font-semibold" onClick={() => navigate(`/jobs/${success.id}`)} data-testid="view-job-button">
            View Job
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto" data-testid="new-job-page">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">New Reshine Job</h1>
      <p className="text-sm text-muted-foreground mt-1">Register a customer article for restoration.</p>

      {/* Customer */}
      <Card className="mt-6">
        <CardContent className="p-4 sm:p-5 space-y-4">
          <h2 className="text-base font-semibold">1. Customer</h2>
          {!existingCustomer && (
            <div className="flex gap-2">
              <div className="flex-1">
                <Label htmlFor="mobile">Mobile Number *</Label>
                <Input id="mobile" data-testid="customer-mobile-input" inputMode="numeric" placeholder="9876543210"
                  value={mobile} onChange={(e) => { setMobile(e.target.value.replace(/\D/g, "").slice(0, 15)); setCustomerChecked(false); }} className="h-12 mt-1" />
              </div>
              <Button onClick={checkCustomer} disabled={checking} className="self-end h-12" variant="secondary" data-testid="check-customer-button">
                {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Check"}
              </Button>
            </div>
          )}

          {dupCustomer && !existingCustomer && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 px-3.5 py-2.5 flex items-start gap-2.5" data-testid="duplicate-customer-warning">
              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                  A customer with this mobile already exists: <span className="font-semibold" data-testid="duplicate-customer-name">{dupCustomer.customer_name}</span>
                </p>
                <p className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-0.5">Use the existing record to avoid duplicate customers.</p>
              </div>
              <Button size="sm" variant="secondary" className="shrink-0 self-center" onClick={useDuplicateCustomer} data-testid="use-existing-customer-button">
                Use existing
              </Button>
            </div>
          )}

          {existingCustomer && (
            <div className="rounded-xl border border-primary/30 bg-accent/50 p-4" data-testid="existing-customer-card">
              <div className="flex items-start gap-3">
                <UserCheck className="h-5 w-5 text-primary mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold">{existingCustomer.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{existingCustomer.mobile} {existingCustomer.city ? `· ${existingCustomer.city}` : ""}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {existingCustomer.total_jobs} total jobs · {existingCustomer.active_jobs} active · {existingCustomer.completed_jobs} completed
                  </p>
                  {existingCustomer.jobs?.slice(0, 3).map((j) => (
                    <div key={j.id} className="flex items-center justify-between gap-2 mt-2 text-xs border-t pt-2">
                      <span className="truncate">{j.job_number} · {j.article_name}</span>
                      <StatusBadge status={j.status} />
                    </div>
                  ))}
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setExistingCustomer(null); setCustomerChecked(false); }} data-testid="change-customer-button">Change</Button>
              </div>
            </div>
          )}

          {customerChecked && !existingCustomer && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="new-customer-fields">
              <div>
                <Label>First Name *</Label>
                <Input data-testid="customer-name-input" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} className="h-12 mt-1" placeholder="First name" />
              </div>
              <div>
                <Label>Surname *</Label>
                <Input data-testid="customer-surname-input" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className="h-12 mt-1" placeholder="Surname" />
              </div>
              <div>
                <Label>WhatsApp Number</Label>
                <Input data-testid="customer-whatsapp-input" inputMode="numeric" value={form.whatsapp} onChange={(e) => set("whatsapp", e.target.value.replace(/\D/g, "").slice(0, 15))} className="h-12 mt-1" placeholder="Same as mobile if blank" />
              </div>
              <div>
                <Label>Email</Label>
                <Input data-testid="customer-email-input" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="h-12 mt-1" placeholder="name@example.com (optional)" />
              </div>
              <div className="sm:col-span-2">
                <Label>Address *</Label>
                <Input data-testid="customer-address-input" value={form.address} onChange={(e) => set("address", e.target.value)} className="h-12 mt-1" placeholder="House / building, street, area" />
              </div>
              <div>
                <Label>City *</Label>
                <Input data-testid="customer-city-input" value={form.city} onChange={(e) => set("city", e.target.value)} className="h-12 mt-1" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>State *</Label>
                  <Select value={form.state} onValueChange={(v) => set("state", v)}>
                    <SelectTrigger data-testid="customer-state-input" className="h-12 mt-1"><SelectValue placeholder="Select state" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {INDIAN_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Pincode *</Label>
                  <Input data-testid="customer-pincode-input" inputMode="numeric" value={form.pincode} onChange={(e) => set("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))} className="h-12 mt-1" />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Articles */}
      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5 space-y-4">
          <h2 className="text-base font-semibold">2. Articles</h2>
          {form.articles.map((art, i) => (
            <div key={art._key || i} className="rounded-xl border p-3.5 space-y-3" data-testid={`article-card-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Article {i + 1}</p>
                {form.articles.length > 1 && (
                  <button type="button" onClick={() => removeArticle(i)} data-testid={`article-remove-${i}`} className="text-red-500 p-1" aria-label="Remove article">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
              <div>
                <Label>Article / Product Name *</Label>
                <Input data-testid={`article-name-input-${i}`} value={art.article_name} onChange={(e) => setArticle(i, "article_name", e.target.value)} className="h-12 mt-1" placeholder="e.g. Brass Tiffin Box" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Product Category *</Label>
                  <Select value={art.category} onValueChange={(v) => setArticle(i, "category", v)}>
                    <SelectTrigger data-testid={`article-category-select-${i}`} className="h-12 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Quantity *</Label>
                  <Input data-testid={`article-quantity-input-${i}`} type="number" min="1" inputMode="numeric" value={art.quantity} onChange={(e) => setArticle(i, "quantity", e.target.value)} className="h-12 mt-1" placeholder="1" />
                </div>
                <div>
                  <Label>Base Material</Label>
                  <Select value={art.base_material} onValueChange={(v) => setArticle(i, "base_material", v)}>
                    <SelectTrigger data-testid={`article-material-select-${i}`} className="h-12 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{MATERIALS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Weight (Kg)</Label>
                  <Input data-testid={`article-weight-input-${i}`} inputMode="decimal" value={art.weight} onChange={(e) => setArticle(i, "weight", e.target.value.replace(/[^\d.]/g, ""))} className="h-12 mt-1" placeholder="e.g. 1.5" />
                </div>
                <div>
                  <Label>Current Condition</Label>
                  <Select value={art.condition} onValueChange={(v) => setArticle(i, "condition", v)}>
                    <SelectTrigger data-testid={`article-condition-select-${i}`} className="h-12 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{CONDITIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Buffing Surface Finish</Label>
                  <Select value={art.surface_finish} onValueChange={(v) => setArticle(i, "surface_finish", v)}>
                    <SelectTrigger data-testid={`article-surface-finish-select-${i}`} className="h-12 mt-1"><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>{SURFACE_FINISHES.map((s) => <SelectItem key={s} value={s} data-testid={`surface-finish-option-${s.toLowerCase()}`}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Required Service / Finish *</Label>
                <div className="flex flex-wrap gap-2 mt-2" data-testid={`finish-options-${i}`}>
                  {FINISHES.map((f) => (
                    <button key={f} type="button" onClick={() => toggleArticleFinish(i, f)}
                      data-testid={`finish-chip-${i}-${f.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
                        (art.finishes || []).includes(f)
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-card hover:bg-accent"
                      }`}>
                      {f}
                    </button>
                  ))}
                </div>
                {(art.finishes || []).includes("Other") && (
                  <Input data-testid={`finish-other-input-${i}`} value={art.finish_other} onChange={(e) => setArticle(i, "finish_other", e.target.value)} className="h-11 mt-2" placeholder="Describe the custom requirement" />
                )}
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={addArticle} data-testid="add-article-button" className="w-full h-12 border-dashed font-semibold">
            <Plus className="h-4 w-4 mr-1.5" /> Add Another Article
          </Button>
          <div>
            <Label>Customer's Special Instructions</Label>
            <Textarea data-testid="special-instructions-input" value={form.special_instructions} onChange={(e) => set("special_instructions", e.target.value)} rows={2} className="mt-1" placeholder="Anything the customer specifically asked for" />
          </div>
        </CardContent>
      </Card>

      {/* Photos */}
      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5 space-y-3">
          <h2 className="text-base font-semibold">3. Product Photos *</h2>
          <p className="text-xs text-muted-foreground">At least one photo is required. Photos are auto-compressed and stored securely.</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-accent/40 h-24 cursor-pointer active:scale-[0.98] transition-transform" data-testid="camera-capture-button">
              <Camera className="h-6 w-6 text-primary" />
              <span className="text-xs font-semibold">Take Photo</span>
              <input type="file" accept="image/*" capture="environment" className="hidden" data-testid="camera-capture-input"
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
            </label>
            <label className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed h-24 cursor-pointer hover:bg-muted active:scale-[0.98] transition-all" data-testid="gallery-upload-button">
              <ImagePlus className="h-6 w-6 text-muted-foreground" />
              <span className="text-xs font-semibold text-muted-foreground">From Gallery</span>
              <input type="file" accept="image/*" multiple className="hidden" data-testid="gallery-upload-input"
                onChange={(e) => { addPhotos(e.target.files); e.target.value = ""; }} />
            </label>
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" data-testid="photo-preview-grid">
              {photos.map((p, i) => (
                <div key={p.preview} className="relative group aspect-square rounded-lg overflow-hidden border" data-testid={`photo-preview-${i}`}>
                  <img src={p.preview} alt="" className="h-full w-full object-cover" />
                  <button onClick={() => removePhoto(i)} data-testid={`photo-remove-${i}`}
                    className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Additional */}
      <Card className="mt-4">
        <CardContent className="p-4 sm:p-5 space-y-3">
          <h2 className="text-base font-semibold">4. Additional Information</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Estimated Value (₹)</Label>
              <Input data-testid="estimated-value-input" inputMode="decimal" value={form.estimated_value} onChange={(e) => set("estimated_value", e.target.value.replace(/[^\d.]/g, ""))} className="h-12 mt-1" placeholder="Optional" />
            </div>
            <div>
              <Label>Advance Payment (₹)</Label>
              <Input data-testid="advance-payment-input" inputMode="decimal" value={form.advance_payment} onChange={(e) => set("advance_payment", e.target.value.replace(/[^\d.]/g, ""))} className="h-12 mt-1" placeholder="Optional" />
            </div>
            <div>
              <Label>Expected Completion Date</Label>
              <Input data-testid="expected-date-input" type="date" min={new Date().toISOString().slice(0, 10)} value={form.expected_completion_date} onChange={(e) => set("expected_completion_date", e.target.value)} className="h-12 mt-1" />
            </div>
          </div>
          {form.estimated_value !== "" && (
            <p className="text-sm rounded-lg border bg-accent/50 px-3.5 py-2.5" data-testid="balance-payment-display">
              Balance Payment: <span className="font-bold">₹{(parseFloat(form.estimated_value || "0") - parseFloat(form.advance_payment || "0")).toLocaleString("en-IN")}</span>
              <span className="text-xs text-muted-foreground"> (Est. ₹{parseFloat(form.estimated_value || "0").toLocaleString("en-IN")} − Advance ₹{parseFloat(form.advance_payment || "0").toLocaleString("en-IN")})</span>
            </p>
          )}
          <p className="text-xs text-muted-foreground" data-testid="tax-note">
            Note: All amounts are exclusive of tax. Applicable tax will be charged additionally.
          </p>
          <div>
            <Label>Staff Remarks (internal)</Label>
            <Textarea data-testid="staff-remarks-input" value={form.staff_remarks} onChange={(e) => set("staff_remarks", e.target.value)} rows={2} className="mt-1" />
          </div>
          <label className="flex items-start gap-3 rounded-xl border p-3.5 cursor-pointer" data-testid="confirm-finish-checkbox-wrapper">
            <Checkbox data-testid="confirm-finish-checkbox" checked={form.confirmed_finish} onCheckedChange={(v) => set("confirmed_finish", !!v)} className="mt-0.5" />
            <span className="text-sm">Customer has confirmed the required finish / service.</span>
          </label>
        </CardContent>
      </Card>

      <Button onClick={submit} disabled={submitting} data-testid="submit-job-button"
        className="w-full h-14 mt-6 text-base font-bold">
        {submitting ? <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Registering…</> : "Submit Job"}
      </Button>
      <p className="text-xs text-muted-foreground text-center mt-3">Form progress is auto-saved on this device.</p>
    </div>
  );
}
